import {
  append,
  compose,
  concat,
  curry,
  equals,
  filter,
  find,
  gt,
  hasIn,
  head,
  merge,
  not,
  omit,
  pipe,
  prop,
  propEq,
  propOr,
  reject,
} from "ramda";

const CARD_TYPES = [
  {
    type: "AX",
    prettyName: "Amex",
    lowercase: "amex",
    maxLength: 15,
    format: "xxxx xxxxxx xxxxx",
    choppedFormat: "xxxx xxxxxx x",
    firstChar: "3",
  },
  {
    type: "VI",
    prettyName: "Visa",
    lowercase: "visa",
    maxLength: 16,
    format: "xxxx xxxx xxxx xxxx",
    choppedFormat: "xxxx xxxx xxxx ",
    firstChar: "4",
  },
  {
    type: "MC",
    prettyName: "MasterCard",
    lowercase: "mastercard",
    maxLength: 16,
    format: "xxxx xxxx xxxx xxxx",
    choppedFormat: "xxxx xxxx xxxx ",
    firstChar: "5",
  },
  {
    type: "DS",
    prettyName: "Discover",
    lowercase: "discover",
    maxLength: 16,
    format: "xxxx xxxx xxxx xxxx",
    choppedFormat: "xxxx xxxx xxxx ",
    firstChar: "6",
  },
];

const findFormattersByType = type => find(propEq("type", type), CARD_TYPES);

const findFormattersByLowercase = lowercaseName =>
  find(propEq("lowercase", lowercaseName), CARD_TYPES);

const findCardTypeByFirstDigit = digit =>
  find(propEq("firstChar", digit.toString()), CARD_TYPES);

const formatObscuredCardNumberByType = (type, lastFour) =>
  concat(findFormattersByType(type).choppedFormat, lastFour);

const formatObscuredCardNumberByLowercase = (lowercaseName, lastFour) =>
  concat(findFormattersByLowercase(lowercaseName).choppedFormat, lastFour);

const formatCardNameByType = type => findFormattersByType(type).prettyName;

const isNotCardCopy = compose(not, propOr(false, "isLocalSavedCopy"));

const removeCardByCardUuid = curry((cardUuid, cards) =>
  reject(propEq("cardUuid", cardUuid), cards)
);
const findCardByCardUuid = curry((cardUuid, cards) =>
  find(propEq("cardUuid", cardUuid), cards)
);

const omitCardCopies = reject(propEq("isLocalSavedCopy", true));
const isolateDisplayableCards = compose(
  omitCardCopies,
  removeCardByCardUuid("new-card")
);

function preserveLocalCopy(state, cardUuid) {
  const activeCardRecords = filter(propEq("cardUuid", cardUuid), state);

  if (
    equals(activeCardRecords.length, 1) &&
    not(equals(cardUuid, "new-card"))
  ) {
    const localCardCopy = merge(head(activeCardRecords), {
      isLocalSavedCopy: true,
    });
    // Create a copy in case the user decides to cancel updates
    return [...state, localCardCopy];
  }
  // A copy already exists
  return state;
}

function discardChanges(state, cardUuid) {
  const activeCardRecords = filter(propEq("cardUuid", cardUuid), state);
  const cleanCards = reject(propEq("cardUuid", cardUuid), state);

  if (gt(activeCardRecords.length, 1)) {
    const existingRecord = pipe(
      reject(isNotCardCopy), // Get rid of the object storing local changes
      head,
      omit(["isLocalSavedCopy"])
    )(activeCardRecords);

    return append(existingRecord, cleanCards);
  }
  if (hasIn("isBeingEdited", head(activeCardRecords))) {
    // User hit edit but made no changes
    return [...cleanCards, omit(["isBeingEdited"], head(activeCardRecords))];
  }
  // There is only 1 copy of the card
  return state;
}

function handleErrors(key, card) {
  if (card.errors) return prop(key, card.errors);
  return null;
}

export {
  omitCardCopies,
  isolateDisplayableCards,
  findCardByCardUuid,
  findCardTypeByFirstDigit,
  findFormattersByLowercase,
  isNotCardCopy,
  removeCardByCardUuid,
  findFormattersByType,
  discardChanges,
  preserveLocalCopy,
  handleErrors,
  formatCardNameByType,
  formatObscuredCardNumberByType,
  formatObscuredCardNumberByLowercase,
};
